import { Injectable, inject, signal } from "@angular/core";
import { HttpBackend, HttpClient, HttpHeaders } from "@angular/common/http";
import { firstValueFrom, Subject } from "rxjs";
import { io, type Socket } from "socket.io-client";
import { environment } from "@infra-env/environment";
import { AuthManager } from "@infra-adapters/services/auth.service";
import { StorageKeys } from "@core-interfaces/global";
import { canReviewPublicRegistrationsFromSession } from "@core-interfaces/user-admin/user-admin.permissions";
import { MedicalRecordBackofficePermissionsPolicy } from "@core-rules/medical-record/medical-record-backoffice-permissions.policy";
import {
  MEDICAL_RECORD_PENDING_REVIEW_EVENT,
  PUBLIC_REGISTRATION_NEW_PENDING_EVENT,
} from "./public-registration-realtime.events";
import { formatAppDateTime } from "@infra-adapters/utils/app-datetime.display";

export type RegistrationReviewAlertItem = {
  id: string;
  title: string;
  icon: string;
  color: string;
};

const MAX_RECENT = 15;

/** Lo consume `DefaultLayoutComponent` con `c-toaster` (CoreUI). */
export type RealtimeToastMetaLine = { label: string; value: string };

export type RealtimeNotificationToastPayload = {
  title: string;
  /** Texto principal (ej. nombre o resumen). */
  body: string;
  color?: string;
  linkCommands?: string[];
  subtitle?: string;
  categoryLabel?: string;
  iconName?: string;
  lines?: RealtimeToastMetaLine[];
  /** Hora breve para pie del toast (ej. "14:32"). */
  timeLabel?: string;
};

function nowTimeLabel(): string {
  try {
    const s = formatAppDateTime(new Date());
    return s === "—" ? "" : s;
  } catch {
    return "";
  }
}

function authHeaders(token: string): HttpHeaders {
  return new HttpHeaders({ Authorization: `Bearer ${token}` });
}

function alertTitleFromPending(row: Record<string, unknown>): string {
  const first = String(row["firstName"] ?? "").trim();
  const last = String(row["lastName"] ?? "").trim();
  const fullName = `${first} ${last}`.trim();
  const email = String(row["email"] ?? "").trim();
  return fullName || email || "Solicitud de registro";
}

function createdAtMs(row: Record<string, unknown>): number {
  const raw = row["createdAt"];
  if (raw == null || raw === "") {
    return 0;
  }
  const t = new Date(String(raw)).getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * Deduplica por id (última aparición gana) y ordena por createdAt desc.
 */
function pendingRowsToAlerts(
  rows: unknown[],
): RegistrationReviewAlertItem[] {
  const byId = new Map<string, { row: Record<string, unknown>; at: number }>();
  for (const raw of rows) {
    if (!raw || typeof raw !== "object") {
      continue;
    }
    const row = raw as Record<string, unknown>;
    const id = String(row["id"] ?? row["_id"] ?? "").trim();
    if (!id) {
      continue;
    }
    byId.set(id, { row, at: createdAtMs(row) });
  }
  return [...byId.entries()]
    .sort((a, b) => b[1].at - a[1].at)
    .slice(0, MAX_RECENT)
    .map(([id, { row }]) => ({
      id,
      title: alertTitleFromPending(row),
      icon: "cilUserFollow",
      color: "success",
    }));
}

@Injectable({
  providedIn: "root",
})
export class RegistrationReviewNotificationsService {
  private readonly auth = inject(AuthManager);
  private readonly prestacionPerms = inject(MedicalRecordBackofficePermissionsPolicy);
  /**
   * Cliente HTTP sin interceptores (vía HttpBackend) para no crear un ciclo:
   * HttpClient → authErrorInterceptor → este servicio → UserAdminApiService → HttpClient.
   */
  private readonly http = new HttpClient(inject(HttpBackend));
  private socket: Socket | null = null;
  /** Evita refrescos concurrentes que reescriban la lista a medias. */
  private inboxRefreshInFlight: Promise<void> | null = null;
  private socketHandlersBound = false;

  /**
   * Cantidad de solicitudes pendientes (= largo coherente con la lista de la campanita).
   * Se mantiene el nombre histórico `unreadCount` por compatibilidad con el header.
   */
  readonly unreadCount = signal(0);
  readonly recentAlerts = signal<RegistrationReviewAlertItem[]>([]);
  readonly prestacionRecentAlerts = signal<RegistrationReviewAlertItem[]>([]);

  readonly toastRequest$ = new Subject<RealtimeNotificationToastPayload>();
  private desktopPermissionRequested = false;

  private emitPopupToast(payload: RealtimeNotificationToastPayload): void {
    this.toastRequest$.next(payload);
    this.emitDesktopNotification(payload);
  }

  private notificationApiAvailable(): boolean {
    return typeof window !== "undefined" && "Notification" in window;
  }

  private requestDesktopPermissionIfNeeded(): void {
    if (!this.notificationApiAvailable()) {
      return;
    }
    if (this.desktopPermissionRequested) {
      return;
    }
    this.desktopPermissionRequested = true;
    if (Notification.permission === "default") {
      void Notification.requestPermission().catch(() => {
        /* ignore */
      });
    }
  }

  private emitDesktopNotification(payload: RealtimeNotificationToastPayload): void {
    if (!this.notificationApiAvailable()) {
      return;
    }
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      return;
    }
    if (Notification.permission !== "granted") {
      return;
    }
    try {
      const browserNotification = new Notification(payload.title, {
        body: payload.subtitle ? `${payload.body}\n${payload.subtitle}` : payload.body,
        tag: payload.linkCommands?.join("|") || payload.title,
      });
      browserNotification.onclick = () => {
        try {
          window.focus();
        } catch {
          /* ignore */
        }
        browserNotification.close();
      };
    } catch {
      /* ignore */
    }
  }

  /** Base URL del API sin `/api` (mismo host que sirve Socket.IO). */
  private socketBaseUrl(): string {
    const envExt = environment as { socketUrl?: string };
    const explicit = envExt.socketUrl?.trim();
    if (explicit) {
      return explicit.replace(/\/$/, "");
    }
    const api = environment.apiUrl?.trim() ?? "";
    if (api && /^https?:\/\//i.test(api)) {
      return api.replace(/\/api\/?$/i, "");
    }
    if (typeof window !== "undefined") {
      return window.location.origin;
    }
    return "";
  }

  private wantsSocket(session: ReturnType<AuthManager["UserSessionData"]>): boolean {
    if (!session) {
      return false;
    }
    return (
      canReviewPublicRegistrationsFromSession(session) ||
      this.prestacionPerms.canValidatePrestacionesFromSession(session)
    );
  }

  async syncWithSession(): Promise<void> {
    const session = this.auth.UserSessionData();
    const token = sessionStorage.getItem(StorageKeys.session_token);
    if (!token || !session || !this.wantsSocket(session)) {
      this.disconnect();
      this.unreadCount.set(0);
      this.recentAlerts.set([]);
      this.prestacionRecentAlerts.set([]);
      return;
    }
    if (!this.prestacionPerms.canValidatePrestacionesFromSession(session)) {
      this.prestacionRecentAlerts.set([]);
    }
    if (!canReviewPublicRegistrationsFromSession(session)) {
      this.unreadCount.set(0);
      this.recentAlerts.set([]);
    }
    this.ensureSocket(token);
    if (canReviewPublicRegistrationsFromSession(session)) {
      await this.refreshRegistrationInbox();
    }
    this.requestDesktopPermissionIfNeeded();
  }

  private ensureSocket(token: string): void {
    const url = this.socketBaseUrl();
    if (!url) {
      return;
    }

    if (this.socket?.connected) {
      this.socket.auth = { token };
      return;
    }

    this.disconnect();
    this.socket = io(url, {
      path: "/socket.io",
      auth: { token },
      transports: ["websocket", "polling"],
    });
    this.bindSocketHandlers();
  }

  private bindSocketHandlers(): void {
    if (!this.socket || this.socketHandlersBound) {
      return;
    }
    this.socketHandlersBound = true;

    this.socket.on(
      PUBLIC_REGISTRATION_NEW_PENDING_EVENT,
      (payload: {
        registrationId?: string;
        email?: string;
        fullName?: string;
      }) => {
        // Fuente de verdad = API pending (evita duplicados / ítems fantasma).
        void this.refreshRegistrationInbox().then(() => {
          const id = payload?.registrationId?.trim();
          if (!id) {
            return;
          }
          const fullName = (payload.fullName && payload.fullName.trim()) || "";
          const email = (payload.email && payload.email.trim()) || "";
          this.emitPopupToast({
            title: "Nueva solicitud de registro",
            body: fullName || email || "Solicitud sin nombre asociado",
            subtitle:
              "Ya puedes revisar los datos y decidir si apruebas o rechazas el registro.",
            categoryLabel: "Registro público",
            iconName: "cilUserFollow",
            color: "success",
            lines: [
              { label: "Correo", value: email || "No informado" },
              { label: "Solicitud", value: id },
            ],
            timeLabel: nowTimeLabel(),
            linkCommands: ["/solicitudes-registro", id],
          });
        });
      },
    );

    this.socket.on(
      MEDICAL_RECORD_PENDING_REVIEW_EVENT,
      (payload: { medicalRecordId?: string; status?: string }) => {
        const id = payload?.medicalRecordId;
        if (!id) {
          return;
        }
        const statusLabel = payload?.status
          ? ` (${payload.status})`
          : "";
        this.prestacionRecentAlerts.update((list) => {
          const next: RegistrationReviewAlertItem[] = [
            {
              id,
              title: `Prestación pendiente de revisión${statusLabel}`,
              icon: "cilNotes",
              color: "warning",
            },
            ...list.filter((x) => x.id !== id),
          ];
          return next.slice(0, MAX_RECENT);
        });
        const status =
          (payload?.status && String(payload.status).trim()) || "";
        this.emitPopupToast({
          title: "Prestación pendiente de revisión",
          body: status
            ? `Estado actual: ${status}`
            : "Requiere revisión para continuar.",
          subtitle:
            "Se registró o actualizó una prestación y necesita revisión.",
          categoryLabel: "Prestaciones",
          iconName: "cilNotes",
          color: "warning",
          lines: [
            { label: "Prestación", value: id },
            {
              label: "Estado",
              value: status || "No informado",
            },
          ],
          timeLabel: nowTimeLabel(),
          linkCommands: ["/prestaciones", id],
        });
      },
    );
  }

  disconnect(): void {
    this.socket?.removeAllListeners();
    this.socket?.disconnect();
    this.socket = null;
    this.socketHandlersBound = false;
  }

  /**
   * Compat: recarga inbox de solicitudes (lista + contador pendientes).
   */
  async refreshUnreadCount(): Promise<void> {
    await this.refreshRegistrationInbox();
  }

  /**
   * Sincroniza campanita con `GET /user/public-registrations/pending`.
   * Reemplaza la lista completa (sin merge optimista) para no duplicar.
   */
  async refreshRegistrationInbox(): Promise<void> {
    if (this.inboxRefreshInFlight) {
      return this.inboxRefreshInFlight;
    }
    this.inboxRefreshInFlight = this.doRefreshRegistrationInbox().finally(() => {
      this.inboxRefreshInFlight = null;
    });
    return this.inboxRefreshInFlight;
  }

  private async doRefreshRegistrationInbox(): Promise<void> {
    const session = this.auth.UserSessionData();
    if (!session || !canReviewPublicRegistrationsFromSession(session)) {
      this.unreadCount.set(0);
      this.recentAlerts.set([]);
      return;
    }
    const token = sessionStorage.getItem(StorageKeys.session_token);
    if (!token) {
      this.unreadCount.set(0);
      this.recentAlerts.set([]);
      return;
    }
    try {
      const url = `${environment.apiUrl}/user/public-registrations/pending`;
      const body = await firstValueFrom(
        this.http.get<unknown>(url, { headers: authHeaders(token) }),
      );
      const rows = Array.isArray(body)
        ? body
        : Array.isArray((body as { data?: unknown })?.data)
          ? ((body as { data: unknown[] }).data)
          : Array.isArray((body as { items?: unknown })?.items)
            ? ((body as { items: unknown[] }).items)
            : [];
      const alerts = pendingRowsToAlerts(rows);
      this.recentAlerts.set(alerts);
      // Contador = pendientes reales (misma fuente que la lista / página).
      this.unreadCount.set(
        Array.isArray(rows)
          ? new Set(
              rows
                .map((r) => {
                  if (!r || typeof r !== "object") return "";
                  const o = r as Record<string, unknown>;
                  return String(o["id"] ?? o["_id"] ?? "").trim();
                })
                .filter(Boolean),
            ).size
          : alerts.length,
      );
    } catch {
      /* ignore: no borrar lista previa ante fallo de red puntual */
    }
  }

  clearRecentAlerts(): void {
    this.recentAlerts.set([]);
    this.unreadCount.set(0);
  }

  /** Quita una solicitud de la campanita (p. ej. tras aprobar/rechazar). */
  removeRegistrationAlert(id: string): void {
    const next = this.recentAlerts().filter((x) => x.id !== id);
    this.recentAlerts.set(next);
    this.unreadCount.set(next.length);
  }

  clearPrestacionAlerts(): void {
    this.prestacionRecentAlerts.set([]);
  }

  removePrestacionAlert(id: string): void {
    this.prestacionRecentAlerts.update((list) => list.filter((x) => x.id !== id));
  }
}
